import { useState, useEffect } from "react";
import API from "@/lib/api";
import { MapPin, Plus, Search, Edit2, Trash2, X, Check, Loader2 } from "lucide-react";

export default function AdminDistributors() {
  const [distributors, setDistributors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", db_code: "", db_type: "Trade", division: "", zone: "", address: "", pin_code: "", latitude: "", longitude: "" });

  useEffect(() => { loadData(); }, [search, page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (search) params.append("q", search);
      const { data } = await API.get(`/admin/distributors?${params}`);
      setDistributors(data.distributors || []);
      setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, latitude: parseFloat(form.latitude) || 0, longitude: parseFloat(form.longitude) || 0 };
      if (editing) { await API.put(`/admin/distributors/${editing}`, payload); }
      else { await API.post("/admin/distributors", payload); }
      setShowForm(false); setEditing(null);
      setForm({ name: "", db_code: "", db_type: "Trade", division: "", zone: "", address: "", pin_code: "", latitude: "", longitude: "" });
      loadData();
    } catch {}
  };

  const handleDelete = async (id) => { if (window.confirm("Delete?")) { await API.delete(`/admin/distributors/${id}`); loadData(); } };

  const startEdit = (d) => {
    setForm({ name: d.name || "", db_code: d.db_code || "", db_type: d.db_type || "Trade", division: d.division || "", zone: d.zone || "", address: d.address || "", pin_code: d.pin_code || "", latitude: String(d.latitude || ""), longitude: String(d.longitude || "") });
    setEditing(d.id); setShowForm(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-distributors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><MapPin className="w-6 h-6 text-[#008A99]" /><div><h1 className="text-2xl font-bold text-gray-900">Distributor Master</h1><p className="text-sm text-gray-500">{total} distributors</p></div></div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: "", db_code: "", db_type: "Trade", division: "", zone: "", address: "", pin_code: "", latitude: "", longitude: "" }); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-semibold" data-testid="add-distributor-btn"><Plus className="w-4 h-4" /> Add</button>
      </div>

      <div className="relative"><Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search distributors..."
          className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="distributor-search" /></div>

      {showForm && (
        <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="distributor-form">
          <div className="flex justify-between"><h3 className="font-semibold">{editing ? "Edit" : "Add"} Distributor</h3><button onClick={() => setShowForm(false)}><X className="w-4 h-4" /></button></div>
          <div className="grid grid-cols-2 gap-3">
            {[["name","Name *"],["db_code","DB Code *"],["db_type","Type"],["division","Division"],["zone","Zone"],["pin_code","PIN Code"],["latitude","Latitude"],["longitude","Longitude"]].map(([k,l]) => (
              <div key={k}><label className="text-xs font-semibold text-gray-500 uppercase">{l}</label>
                <input type="text" value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-[#008A99]" /></div>
            ))}
          </div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase">Address</label>
            <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} rows={2} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-[#008A99] resize-none" /></div>
          <button onClick={handleSave} className="px-6 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-semibold flex items-center gap-2"><Check className="w-4 h-4" /> Save</button>
        </div>
      )}

      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b"><th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Name</th><th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Code</th><th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Type</th><th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Address</th><th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">PIN</th><th className="px-3 py-2.5"></th></tr></thead>
            <tbody>{distributors.map(d => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-3 py-2.5 font-medium">{d.name}</td>
                <td className="px-3 py-2.5 text-gray-500">{d.db_code}</td>
                <td className="px-3 py-2.5"><span className="text-[10px] px-1.5 py-0.5 bg-[#E0F4F5] text-[#008A99] rounded font-medium">{d.db_type}</span></td>
                <td className="px-3 py-2.5 text-gray-500 max-w-xs truncate">{d.address}</td>
                <td className="px-3 py-2.5 text-gray-400">{d.pin_code}</td>
                <td className="px-3 py-2.5"><div className="flex gap-1"><button onClick={() => startEdit(d)} className="p-1.5 hover:bg-gray-100 rounded"><Edit2 className="w-3.5 h-3.5 text-gray-400" /></button><button onClick={() => handleDelete(d.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button></div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {total > 30 && <div className="flex justify-center gap-2"><button onClick={() => setPage(Math.max(1, page-1))} disabled={page===1} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-30">Prev</button><span className="text-sm text-gray-500 py-1.5">Page {page}</span><button onClick={() => setPage(page+1)} className="px-3 py-1.5 border rounded-lg text-sm">Next</button></div>}
    </div>
  );
}
